import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { 
  WaterSubDevAreaDetailsRequest,
  WaterSubDevAreaDropDownRequest,
  WaterSubDevAreaCascadingFilterRequest,
  WaterSubDevAreaInsertUpdateRequest,
  WaterSubDevAreaRemoveRequest,
  WaterSubDevAreaDetail,
  WaterSubDevAreaDetailsResponse,
  WaterSubDevAreaDropDownResponse,
  WaterSubDevAreaCascadingFilterResponse,
  WaterSubDevAreaRow,
  WaterSubDevAreaDropDownResult,
  DropdownColumn,
  FilterColumn
} from './interfaces/water-sub-dev-area.interfaces';
import { ErrorHandler } from '../utils/error-handler';
import { environment } from '../environments/environments';

@Injectable({
  providedIn: 'root',
})
export class WaterSubDevAreaService {
  private baseUrl = environment.WebApiURL;
  private readonly waterSubDevAreaExportTabName = 'WaterSubDevAreaMapping';

  private detailsUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterSubDevAreaDetails`;
  }

  private dropdownUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterSubDevAreaDropDown`;
  }

  private cascadingFilterUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterSubDevAreaCascadingFilter`;
  }

  private insertUpdateUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/InsertUpdateWaterSubDevArea`;
  }

  private removeUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/RemoveWaterSubDevAreaDetails`;
  }

  private exportUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  constructor(private http: HttpClient) {}

  async exportAllWaterSubDevAreaData(): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl(), { tabName: this.waterSubDevAreaExportTabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackFileName = `water-sub-dev-area${inferredExtension}`;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return { blob, fileName };
  }

  private normalizeText(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  private splitCtbValues(value: string | null | undefined): string[] {
    return (value ?? '')
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }

  private parseDropdownResponse(response: WaterSubDevAreaDropDownResponse): WaterSubDevAreaDropDownResult {
    if (!response?.waterSubDevAreaColumnFilterItems) {
      return { developmentAreas: [], ctbValues: [] };
    }

    const findColumn = (name: string): DropdownColumn | undefined =>
      response.waterSubDevAreaColumnFilterItems.find((item: DropdownColumn) => 
        (item.columnName || '').toLowerCase() === name.toLowerCase()
      );

    const devAreaItem = findColumn('DevelopmentArea') || findColumn('DevArea');
    const ctbItem = findColumn('CTB') || findColumn('WellPropID') || findColumn('WellPropId');

    return {
      developmentAreas: devAreaItem?.values || [],
      ctbValues: ctbItem?.values || [],
    };
  }

  async getWaterSubDevAreaDetails(filters?: Partial<WaterSubDevAreaDetailsRequest>): Promise<{
    data: WaterSubDevAreaRow[];
    nextRowNumber: number | null;
  }> {
    try {
      const requestBody: WaterSubDevAreaDetailsRequest = {
        developmentArea: filters?.developmentArea || null,
        subDevAreaName: filters?.subDevAreaName || null,
        ctb: filters?.ctb || null,
        lastRowNumber: filters?.lastRowNumber ?? 0
      };

      const response = await firstValueFrom(
        this.http.post<WaterSubDevAreaDetailsResponse>(this.detailsUrl(), requestBody)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      if (parsed.success || parsed.statusCode === 404) {
        const groupedData = this.groupCtbBySubDevArea(response?.waterSubDevAreaDetails || []);
        return {
          data: groupedData,
          nextRowNumber: response?.nextRowNumber ?? null
        };
      }

      console.warn('API returned unexpected status:', parsed.statusCode, parsed.message);
      return { data: [], nextRowNumber: null };
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterSubDevAreaService.getWaterSubDevAreaDetails');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  private groupCtbBySubDevArea(details: WaterSubDevAreaDetail[]): WaterSubDevAreaRow[] {
    const grouped = new Map<string, WaterSubDevAreaRow>();

    details.forEach(item => {
      const key = `${item.developmentArea}||${item.subDevAreaName}`;
      const parsedCtbValues = this.splitCtbValues(item.ctb);
      
      if (grouped.has(key)) {
        const existing = grouped.get(key)!;
        parsedCtbValues.forEach((ctbValue) => {
          if (!existing.ctbs.includes(ctbValue)) {
            existing.ctbs.push(ctbValue);
          }
        });
      } else {
        grouped.set(key, {
          _rowId: this.generateRowId(),
          developmentArea: this.normalizeText(item.developmentArea),
          subDevAreaName: this.normalizeText(item.subDevAreaName),
          ctbs: parsedCtbValues,
          isNew: false,
          _rowEdited: false,
          original: {}
        });
      }
    });

    return Array.from(grouped.values());
  }

  async getWaterSubDevAreaDropDown(filters?: WaterSubDevAreaDropDownRequest): Promise<WaterSubDevAreaDropDownResult> {
    try {
      const requestBody: WaterSubDevAreaDropDownRequest = {
        developmentArea: filters?.developmentArea || null,
        ctb: filters?.ctb || null
      };

      const response = await firstValueFrom(
        this.http.post<WaterSubDevAreaDropDownResponse>(this.dropdownUrl(), requestBody)
      );

      return this.parseDropdownResponse(response);
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterSubDevAreaService.getWaterSubDevAreaDropDown');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getCascadingFilterOptions(filters?: Partial<WaterSubDevAreaCascadingFilterRequest>): Promise<FilterColumn[]> {
    try {
      const requestBody: WaterSubDevAreaCascadingFilterRequest = {
        developmentArea: filters?.developmentArea || null,
        subDevAreaName: filters?.subDevAreaName || null,
        ctb: filters?.ctb || null
      };

      const response = await firstValueFrom(
        this.http.post<WaterSubDevAreaCascadingFilterResponse>(this.cascadingFilterUrl(), requestBody)
      );

      return response?.waterSubDevAreaFilter || [];
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterSubDevAreaService.getCascadingFilterOptions');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async insertUpdateWaterSubDevArea(row: WaterSubDevAreaRow, action: 'insert' | 'update', userName: string, oldSubDevAreaName?: string): Promise<any> {
    try {
      const requestBody: WaterSubDevAreaInsertUpdateRequest = {
        action: action,
        developmentArea: row.developmentArea,
        subDevAreaName: row.subDevAreaName,
        oldSubDevAreaName: action === 'insert' ? null : (oldSubDevAreaName || row.subDevAreaName),
        ctbs: row.ctbs,
        user: userName
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.insertUpdateUrl(), requestBody)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      console.error('Error saving water sub dev area:', error);
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async removeWaterSubDevAreaDetails(row: WaterSubDevAreaRow, userName: string): Promise<any> {
    try {
      const requestBody: WaterSubDevAreaRemoveRequest = {
        developmentArea: row.developmentArea,
        subDevAreaName: row.subDevAreaName,
        user: userName
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.removeUrl(), requestBody)
      );

      if (typeof response === 'string') {
        return {
          statusCode: 200,
          statusMessage: response,
          error: false,
        };
      }

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      console.error('Error deleting water sub dev area:', error);
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  private generateRowId(): string {
    return `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}